import { useCallback, useEffect, useState, useMemo } from 'react';
import { ImageList, Card, ListItemButton, Box, Stack } from '@mui/material';
import { PaymentMethod, PaymentMethodType } from 'orderme-api-integration-client';
import { PaymentClient } from '../../helpers/client';
import { Linear } from '../progressBars/linear';
import { ImageLoader } from '../imgLoader/imgLoader';
import { appStyleMode } from '../../helpers/clientConfigs';
import { AppStyle } from '../../helpers/appStyle';
import { MissingPayments } from './missingPayments';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';

declare let ApplePaySession: any;

export interface PaymentMethodsParam {
  isDisabled?: boolean;
  onPaymentSelected: (paymentId: string) => void;
}

export function PaymentMethods({ onPaymentSelected, isDisabled: isPaymentsDisabled }: PaymentMethodsParam) {
  const paymentMethodsClient: PaymentClient = useMemo(() => new PaymentClient(), []);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>();
  const [selectedPayment, setSelectedPayment] = useState<PaymentMethod>();
  const [allPaymentMethods, setAllPaymentMethods] = useState<PaymentMethod[]>();
  const [missingPayments, setMissingPayments] = useState(false);
  const [isGooglePayEnabled, setIsGooglePayEnabled] = useState(false);
  const isApplePayEnabled = window.ApplePaySession && (window.cordova || ApplePaySession.canMakePayments());

  const googleIsReadyToPayRequest = useMemo(
    () =>
      ({
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [
          {
            tokenizationSpecification: {
              type: 'PAYMENT_GATEWAY',
            },
            type: 'CARD',
            parameters: {
              allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
              allowedCardNetworks: ['VISA', 'MASTERCARD'],
            },
          },
        ],
      }) as google.payments.api.IsReadyToPayRequest,
    []
  );

  const getPayments = useCallback(() => {
    if (window.googlePayClient) {
      window.googlePayClient
        .isReadyToPay(googleIsReadyToPayRequest)
        .then((res) => {
          if (res.result) {
            setIsGooglePayEnabled(true);
          } else {
            setIsGooglePayEnabled(false);
          }
        })
        .catch((err) => {
          console.error(err);
          setIsGooglePayEnabled(false);
        });
    }

    paymentMethodsClient
      .get()
      .then((response) => {
        if (response?.length > 0) {
          setAllPaymentMethods(response);
        } else {
          setMissingPayments(true);
        }
      })
      .catch((err) => {
        console.log(err);
        setMissingPayments(true);
      });
  }, [googleIsReadyToPayRequest, paymentMethodsClient]);

  useEffect(() => {
    getPayments();
  }, [getPayments]);

  const isPaymentEnabled = useCallback(
    (payment: PaymentMethod) => {
      if (payment.id === 'turnedof') {
        return false;
      }

      if (payment.type === PaymentMethodType.ApplyPay) {
        return isApplePayEnabled;
      }

      if (payment.type === PaymentMethodType.GooglePay) {
        return isGooglePayEnabled;
      }

      return true;
    },
    [isApplePayEnabled, isGooglePayEnabled]
  );

  useEffect(() => {
    if (!allPaymentMethods) {
      return;
    }

    var enabledPayments = allPaymentMethods.filter(isPaymentEnabled);
    setPaymentMethods(enabledPayments);
    if (enabledPayments.length === 0) {
      setMissingPayments(true);
    }
  }, [allPaymentMethods, isGooglePayEnabled, isApplePayEnabled, isPaymentEnabled]);

  const selectPayment = (payment: PaymentMethod) => {
    setSelectedPayment(payment);
    onPaymentSelected(payment.id);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {!paymentMethods && !missingPayments && <Linear />}
      {paymentMethods && (
        <ImageList
          rowHeight="auto"
          sx={{
            height: appStyleMode !== AppStyle.Mobile ? 'auto' : null,
            width: '100%',
            mt: '0px',
          }}
          cols={1}
        >
          {paymentMethods.map((paymentMethod, index) => {
            return (
              <Box key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Card
                  sx={{
                    width: '95%',
                    my: '10px',
                  }}
                >
                  <ListItemButton
                    sx={{ p: '8px' }}
                    disabled={isPaymentsDisabled}
                    onClick={() => selectPayment(paymentMethod)}
                  >
                    {selectedPayment === paymentMethod ? (
                      <RadioButtonCheckedIcon color="secondary" />
                    ) : (
                      <RadioButtonUncheckedIcon sx={{ color: 'rgba(0, 0, 0, 0.54);' }} />
                    )}
                    <Stack direction="row" justifyContent="center" alignItems="center" sx={{ width: '90%' }}>
                      {paymentMethod.logoUrls.map((url) => {
                        return (
                          <ImageLoader
                            key={url}
                            src={url}
                            alt={paymentMethod.name}
                            sx={{
                              height: '30px',
                              maxWidth: '90%',
                              objectFit: 'contain',
                              display: 'block',
                              flexShrink: 0,
                            }}
                          />
                        );
                      })}
                    </Stack>
                  </ListItemButton>
                </Card>
              </Box>
            );
          })}
        </ImageList>
      )}
      {missingPayments && <MissingPayments />}
    </Box>
  );
}
